// frontend/admin-portal/src/components/settings/sections/GeneralSettings.jsx
import React, { useState } from "react";
import SettingItem from "../items/SettingItem";
import TextInput from "../items/TextInput";
import SelectInput from "../items/SelectInput";
import StoreIdentityPreview from "../StoreIdentityPreview";
import { LogoUploader, Alert } from "@fiberhome/shared-ui";
import { fileService, resolveImageUrl } from "../../../services/fileService";
import { settingsService } from "../../../services/settingsService";

export default function GeneralSettings({ values = {}, onChange }) {
  const set = (k, v) => typeof onChange === "function" && onChange(k, v);
  const [logoError, setLogoError] = useState("");
  const [logoBusy, setLogoBusy] = useState(false);

  // Upload via file-service
  const uploadLogo = async (file) => {
    const uploaded = await fileService.uploadImage(file, {
      entityType: "store",
    });
    if (!uploaded?.url) throw new Error("Upload returned no URL");

    // Immediately persist to the DB — do not wait for "Save Changes"
    setLogoBusy(true);
    setLogoError("");
    try {
      await settingsService.update("store.logo", uploaded.url);
      // Also update the local draft so the preview updates immediately
      set("store.logo", uploaded.url);
    } catch (err) {
      setLogoError(
        "Logo uploaded but could not be saved: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLogoBusy(false);
    }

    return uploaded;
  };

  // Remove logo → also save immediately
  const removeLogo = async () => {
    setLogoBusy(true);
    setLogoError("");
    try {
      await settingsService.update("store.logo", "");
      set("store.logo", "");
    } catch (err) {
      setLogoError(
        "Could not remove logo: " +
          (err.response?.data?.message || err.message),
      );
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <div className="fh-settings-list">
      {/* Store Identity */}
      <div className="fh-settings-section-title">
        Store Identity
        <span className="fh-settings-section-subtitle">
          Shown on invoices, offers, orders, and every exported PDF
        </span>
      </div>

      {logoError && <Alert variant="danger">{logoError}</Alert>}

      <SettingItem label="Store Name" description="Primary display name">
        <TextInput
          value={values["store.name"]}
          onChange={(v) => set("store.name", v)}
        />
      </SettingItem>

      <SettingItem
        label="Legal / Registered Name"
        description="Official business name on invoices"
      >
        <TextInput
          value={values["store.legalName"]}
          onChange={(v) => set("store.legalName", v)}
        />
      </SettingItem>

      <SettingItem label="Tax ID / TIN">
        <TextInput
          value={values["store.taxId"]}
          onChange={(v) => set("store.taxId", v)}
        />
      </SettingItem>

      <SettingItem
        label="Logo"
        description="Uploaded to the file service — saved immediately"
      >
        <LogoUploader
          value={values["store.logo"] || ""}
          onChange={(url) => {
            // Upload happens inside uploadLogo; onChange fires only on remove
            if (!url) removeLogo();
          }}
          upload={uploadLogo}
          resolveUrl={resolveImageUrl}
          size={72}
          disabled={logoBusy}
        />
      </SettingItem>

      <SettingItem label="Address" description="Full postal address">
        <TextInput
          value={values["store.address"]}
          onChange={(v) => set("store.address", v)}
        />
      </SettingItem>

      <SettingItem label="Phone">
        <TextInput
          value={values["store.phone"]}
          onChange={(v) => set("store.phone", v)}
        />
      </SettingItem>

      <SettingItem label="Email">
        <TextInput
          type="email"
          value={values["store.email"]}
          onChange={(v) => set("store.email", v)}
        />
      </SettingItem>

      <SettingItem label="Website">
        <TextInput
          value={values["store.website"]}
          onChange={(v) => set("store.website", v)}
        />
      </SettingItem>

      <div className="fh-setting-item" style={{ gridTemplateColumns: "1fr" }}>
        <StoreIdentityPreview />
      </div>

      {/* Application */}
      <div
        className="fh-settings-section-title"
        style={{ marginTop: "1.5rem" }}
      >
        Application
      </div>

      <SettingItem
        label="Application Name"
        description="Shown in sidebar and browser tab"
      >
        <TextInput
          value={values["app.name"]}
          onChange={(v) => set("app.name", v)}
        />
      </SettingItem>

      <SettingItem
        label="Tagline"
        description="Short subtitle under the app name"
      >
        <TextInput
          value={values["app.tagline"]}
          onChange={(v) => set("app.tagline", v)}
        />
      </SettingItem>

      <SettingItem label="Default Language">
        <SelectInput
          value={values["app.locale"]}
          onChange={(v) => set("app.locale", v)}
          options={[
            { value: "en", label: "English" },
            { value: "ku", label: "Kurdish (کوردی)" },
          ]}
        />
      </SettingItem>

      <SettingItem label="Timezone">
        <TextInput
          value={values["app.timezone"]}
          onChange={(v) => set("app.timezone", v)}
        />
      </SettingItem>

      <SettingItem label="Date Format">
        <TextInput
          value={values["app.dateFormat"]}
          onChange={(v) => set("app.dateFormat", v)}
        />
      </SettingItem>
    </div>
  );
}
